import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";
import { removeDuplicateData } from "../utils/functions";

const initialState = {
  applicants: [],
  loading: false,
  error: null,
  total: 0,
};

const FETCH_APPLICANTS_REQUEST = "FETCH_APPLICANTS_REQUEST";
const FETCH_APPLICANTS_SUCCESS = "FETCH_APPLICANTS_SUCCESS";
const FETCH_APPLICANTS_FAILURE = "FETCH_APPLICANTS_FAILURE";
const CREATE_APPLICANT_REQUEST = "CREATE_APPLICANT_REQUEST";
const CREATE_APPLICANT_SUCCESS = "CREATE_APPLICANT_SUCCESS";
const CREATE_APPLICANT_FAILURE = "CREATE_APPLICANT_FAILURE";
const UPDATE_APPLICANT_REQUEST = "UPDATE_APPLICANT_REQUEST";
const UPDATE_APPLICANT_SUCCESS = "UPDATE_APPLICANT_SUCCESS";
const UPDATE_APPLICANT_FAILURE = "UPDATE_APPLICANT_FAILURE";
const DELETE_APPLICANT_REQUEST = "DELETE_APPLICANT_REQUEST";
const DELETE_APPLICANT_SUCCESS = "DELETE_APPLICANT_SUCCESS";
const DELETE_APPLICANT_FAILURE = "DELETE_APPLICANT_FAILURE";

export const getApplicants =
  (
    page = 0,
    pageSize = 20,
    sortField = "id",
    sortDirection = "asc",
    filters = {},
    userAgency
  ) =>
  async (dispatch) => {
    const sortFieldMap = {
      "ethnicityInfo.description": "ethnicity",
      "genderInfo.description": "gender",
      "applicantStatusInfo.description": "status",
    };
    sortField = sortFieldMap[sortField] || sortField;
    const service = getService(serviceNames.perApplicant);
    const query = {
      $limit: pageSize,
      $skip: page * pageSize,
      $sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
      // lastName: { $like: '%Sharma%'}, // for searching in the fields
      isDeleted: false,
      agencyId: userAgency,
    };

    const textFields = ["ssn", "lastName", "firstName", "middleName"];

    textFields.forEach((field) => {
      const value = filters[field];
      if (value) {
        query[field] = filters.exactMatch ? value : { $like: `%${value}%` };
      }
    });

    const getQueryValue = (input) => {
      if (Array.isArray(input)) {
        const values = input.map((i) => i.value).filter(Boolean);
        return values.length > 0 ? { $in: values } : undefined;
      } else if (input?.value) {
        return input.value;
      }
      return undefined;
    };

    const dropdownFields = [
      "status",
      "yearApplied",
      "pat",
      "written",
      "polygraph",
      "background",
      "oralCommittee",
      "oralDirector",
      "medical",
      "psych",
    ];

    dropdownFields.forEach((field) => {
      const value = getQueryValue(filters[field]);
      if (value !== undefined) {
        query[field] = value;
      }
    });

    if (Array.isArray(filters.tagTypes) && filters.tagTypes.length > 0) {
      query.tagTypes = filters.tagTypes.map((t) => t.value);
    }

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_APPLICANTS_REQUEST,
        success: FETCH_APPLICANTS_SUCCESS,
        failure: FETCH_APPLICANTS_FAILURE,
      },
      apiCall: () => service.find({ query }),
      transformResult: (res) => {
        return {
          applicants: removeDuplicateData(res.data),
          total: res.total,
        };
      },
      errorMessagePrefix: "Failed to fetch applicants",
    });
  };

export const createApplicant = (applicantData) => async (dispatch) => {
  const service = getService(serviceNames.perApplicant);
  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: CREATE_APPLICANT_REQUEST,
      success: CREATE_APPLICANT_SUCCESS,
      failure: CREATE_APPLICANT_FAILURE,
    },
    apiCall: () => service.create(applicantData),
    successMessage: "Applicant created successfully",
    errorMessagePrefix: "Failed to create applicant",
  });
};

export const updateApplicant = (id, applicantData) => async (dispatch) => {
  const service = getService(serviceNames.perApplicant);
  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: UPDATE_APPLICANT_REQUEST,
      success: UPDATE_APPLICANT_SUCCESS,
      failure: UPDATE_APPLICANT_FAILURE,
    },
    apiCall: () => service.patch(id, applicantData),
    successMessage: "Applicant updated successfully",
    errorMessagePrefix: "Failed to update applicant",
  });
};

export const deleteApplicant = (id, userId) => async (dispatch) => {
  const service = getService(serviceNames.perApplicant);
  const patchData = {
    isDeleted: true,
    deletedBy: userId,
    deletedAt: new Date().toISOString(),
  };

  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: DELETE_APPLICANT_REQUEST,
      success: DELETE_APPLICANT_SUCCESS,
      failure: DELETE_APPLICANT_FAILURE,
    },
    apiCall: () => service.patch(id, patchData),
    transformResult: () => id,
    successMessage: "Applicant deleted successfully",
    errorMessagePrefix: "Failed to delete applicant",
  });
};

export default function applicantsReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_APPLICANTS_REQUEST:
    case CREATE_APPLICANT_REQUEST:
    case UPDATE_APPLICANT_REQUEST:
    case DELETE_APPLICANT_REQUEST:
      return { ...state, loading: true, error: null };
    case FETCH_APPLICANTS_SUCCESS:
      return {
        ...state,
        loading: false,
        applicants: action.payload.applicants,
        total: action.payload.total,
      };
    case CREATE_APPLICANT_SUCCESS:
      return {
        ...state,
        loading: false,
        applicants: [...state.applicants, action.payload],
        total: state.total + 1,
      };
    case UPDATE_APPLICANT_SUCCESS:
      return {
        ...state,
        loading: false,
        applicants: state.applicants.map((applicant) =>
          applicant.id === action.payload.id ? action.payload : applicant
        ),
      };
    case DELETE_APPLICANT_SUCCESS:
      return {
        ...state,
        loading: false,
        applicants: state.applicants.filter(
          (applicant) => applicant.id !== action.payload
        ),
        total: state.total - 1,
      };
    case FETCH_APPLICANTS_FAILURE:
    case CREATE_APPLICANT_FAILURE:
    case UPDATE_APPLICANT_FAILURE:
    case DELETE_APPLICANT_FAILURE:
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}
